import { type CollectionReference, doc, getDoc } from "firebase/firestore"
import { useCallback, useReducer } from "react"

import { type FirebaseDocument, makeConverter } from "../dbUtils"
import { db } from "../firebaseApp"

export interface LoadState<T> {
  loading?: boolean
  error?: Error
  item?: T
}

interface Result<T> {
  items: Record<string, LoadState<T>>
  loadItem: (oid: string) => Promise<void>
}

interface UpdateAction<T> {
  oid: string
  loadState: LoadState<T>
}

const loadReducer = <T>(
  state: Record<string, LoadState<T>>,
  action: UpdateAction<T>,
): Record<string, LoadState<T>> => {
  return {
    ...state,
    [action.oid]: action.loadState,
  }
}

export const useIncrementalLoader = <T extends FirebaseDocument>(
  collection: string | CollectionReference<T>,
): Result<T> => {
  const [items, dispatchItemsUpdate] = useReducer(loadReducer<T>, {})

  const loadItem = useCallback(
    async (oid: string) => {
      if (items[oid]) {
        return
      }

      // Start loading.
      dispatchItemsUpdate({
        oid,
        loadState: { loading: true },
      })
      try {
        const docRef =
          typeof collection === "string"
            ? doc(db, collection, oid)
            : doc(collection, oid)
        const itemDoc = await getDoc(docRef.withConverter(makeConverter<T>()))
        dispatchItemsUpdate({
          oid,
          loadState: {
            loading: false,
            item: itemDoc.data() ?? undefined,
          },
        })
      } catch (error) {
        console.error("Error loading item", error)
        dispatchItemsUpdate({
          oid,
          loadState: { loading: false, error: error as Error },
        })
      }
    },
    [items, collection],
  )

  return { items, loadItem }
}
